import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { SvgIcon } from '@progress/kendo-react-common';
import { homeIcon } from '@progress/kendo-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { DynamicFormFieldModel, DynamicFormModel, ExamModel, FileModel, FormFieldModel, PatientModel, ServiceModel, TagModel } from 'models';

import { FormStates } from 'core/forms';
import { NotificationsService } from 'core/notifications';
import { Breadcrumb, CollapsibleSidebar } from 'core/ui';

import { ActivityLogTab } from 'features/activity-log/fragments/ActivityLogTab';
import { useApiClient, useQueryFilesByExamId } from 'features/api';
import { UserRoles, useCurrentUser } from 'features/auth';
import { getServiceDetailsFromForm } from 'features/dynamic-forms/services/service-details-service';
import { ExamStatusLogTab } from 'features/exam-status-log/fragments/ExamStatusLogTab';
import { DocumentCategories } from 'features/exam/constants';
import { HL7SendHome } from 'features/hl7send/fragments';
import { SendQueue } from 'features/send-queue';
import { HippaLogs } from 'features/share/fragments/HippaLogs';
import { UploadTrackerHome } from 'features/upload-tracker';

import { FileSelectSidebar, FileViewer } from '../../file';
import { SelectPatientModal, usePatientSelectSearch } from '../../patient';
import { validatePatient } from '../../patient/utils';
import { ExamApiStatus } from '../constants';
import { SubmitParams } from '../types';
import { ExamProcessingHeader } from './ExamProcessingHeader';
import { ExamProcessingSidebarBody } from './ExamProcessingSidebarBody';

// Define placeholder types locally
type Integration = Record<string, unknown>;

// Add ExamTagMapping interface
interface ExamTagMapping {
  id?: number;
  examId: number;
  tagId: number;
}

// Define possible tabs
export type ActiveTab = 'files' | 'activityLog' | 'examStatusLog' | 'uploadTracker' | 'sendQueue' | 'hl7Send' | 'hipaaLog';

// Define constants for accordion keys
const LOCATION_ACCORDION_KEY = '2';
const EXAM_ACCORDION_KEY = '3';
const TAGS_ACCORDION_KEY = '4';
const PATIENT_ACCORDION_KEY = '1';
const EDIT_EXAM_ACTIVE_KEYS_INITIAL = [LOCATION_ACCORDION_KEY, EXAM_ACCORDION_KEY]; // Initial state for edit
const ADD_EXAM_ACTIVE_KEYS = [PATIENT_ACCORDION_KEY, LOCATION_ACCORDION_KEY, EXAM_ACCORDION_KEY];

export const ExamProcessing: React.FC = () => {
  const { id } = useParams();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [exam, setExam] = useState<ExamModel | undefined>();
  const [patient, setPatient] = useState<PatientModel | undefined>();
  const [status, setStatus] = useState<ExamApiStatus | null>(null);
  const [_accessToken, setAccessToken] = useState('');
  const [locationId, setLocationId] = useState<number | undefined>();
  const [_fileIsUploading, setFileIsUploading] = useState<boolean>(false);
  const [currentFile, setCurrentFile] = useState<FileModel | null>(null);
  const [patientIdRequired, setPatientIdRequired] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('files');
  const [lastUploadedFileId, setLastUploadedFileId] = useState<number | null>(null);
  const [pendingFiles, setPendingFiles] = useState<FileModel[]>([]);

  const { currentUser } = useCurrentUser(false);
  const isInternal = useMemo(() => {
    const roles = [UserRoles.ADMIN, UserRoles.SUPPORT];
    return currentUser == null || currentUser === 'anonymous' ? false : roles.includes(currentUser.role ?? '');
  }, [currentUser]);

  // Get access token from API client.
  useEffect(() => {
    (async () => {
      const httpClient = apiClient.httpClient;
      let token = '';
      if (httpClient) {
        token = httpClient.getAccessToken ? await httpClient.getAccessToken() : '';
      }
      setAccessToken(token);
    })();
  }, [apiClient.httpClient]);

  // File management using Tanstack Query
  const examIdNumber = id ? parseInt(id, 10) : 0;
  const { data: fetchedFiles } = useQueryFilesByExamId(examIdNumber);

  // Combine fetched files (for existing exams) and pending files (for new exams)
  const displayFiles = useMemo(() => {
    // If 'id' exists (edit mode), use fetchedFiles.
    // If 'id' does not exist (new mode), use pendingFiles.
    return id ? (fetchedFiles ?? []) : pendingFiles;
  }, [id, fetchedFiles, pendingFiles]);

  // Update currentFile based on displayFiles changes
  useEffect(() => {
    // Use displayFiles for logic. Wait if undefined (initial load state for fetchedFiles)
    if (id && fetchedFiles === undefined) {
      // If files are loading or haven't loaded, clear current file
      setCurrentFile(null);
      return;
    }

    // Prioritize selecting the last uploaded file if its ID is set
    if (lastUploadedFileId !== null) {
      const newlyUploadedFile = displayFiles.find((f) => f.id === lastUploadedFileId);
      if (newlyUploadedFile) {
        setCurrentFile(newlyUploadedFile);
        setLastUploadedFileId(null); // Reset after selection
        return;
      }
      // If the ID was set but the file isn't found (shouldn't happen often), reset the ID
      setLastUploadedFileId(null);
    }

    // Standard logic if no specific file was just uploaded
    const fileExists = displayFiles.some((f) => f.id === currentFile?.id);

    if (displayFiles.length === 0) {
      // No files, clear selection
      setCurrentFile(null);
    } else if ((!currentFile || !fileExists) && displayFiles.length > 0) {
      // Current file is null or no longer exists, select the first one if available
      setCurrentFile(displayFiles[0]);
    }
    // else: currentFile exists and is valid, no change needed
  }, [id, fetchedFiles, pendingFiles, displayFiles, currentFile?.id, lastUploadedFileId]); // Depend on sources and combined list

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Patient selection hook.
  const {
    currentPatient,
    handleCreateNewPatientClick,
    handlePatientSelect,
    handlePatientsSearchDataStateChange,
    patientFormState,
    patientSearchDataState,
    patientSearchText,
    searchPatients,
    setCurrentPatient,
    setPatientFormState,
    setPatientSearchText,
    setShowPatientsDialog,
    showPatientsDialog,
    totalSearchPatients,
  } = usePatientSelectSearch(undefined, locationId, exam?.location?.accountId ?? undefined);

  const isFormInEditMode = id !== undefined;

  // Breadcrumbs now include both Home and Exams.
  const breadcrumbs = useMemo(() => {
    return [
      {
        id: 'home',
        text: 'Home',
        icon: <StyledIcon icon={homeIcon} />,
      },
      {
        id: 'exams',
        text: 'Exams',
        navigateTo: '/exam/',
      },
    ];
  }, []);

  const mapDataToNameValuePair = (data: DynamicFormFieldModel): DynamicFormFieldModel[] =>
    _map(data, (value, key) => ({
      id: parseInt(key, 10),
      categoryId: 0,
      description: '',
      label: key,
      name: key,
      value: value != null ? String(value) : '',
      type: 'string',
      dataField: key,
      active: true,
      field: key as unknown as FormFieldModel,
    }));

  // -----------------------------
  // Tagging Feature Integration
  // -----------------------------
  // State for hierarchical tags.
  const [tags, setTags] = useState<TagModel[]>([]);
  // State for tracking which tag items are checked.
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  // State for the tag search term.
  const [searchTerm, setSearchTerm] = useState('');

  // Recursively flatten the checklist data.
  const flattenChecklist = (items: TagModel[]): TagModel[] =>
    (items || []).reduce((acc: TagModel[], item) => {
      acc.push(item);
      if (item.children) {
        acc.push(...flattenChecklist(item.children));
      }
      return acc;
    }, []);

  // Filter checklist data based on the search term.
  const filterChecklist = (items: TagModel[]): TagModel[] => {
    return (items || [])
      .map((item) => {
        // Safely check if name exists and is a string before calling toLowerCase
        const itemName = item?.name;
        const itemMatches = typeof itemName === 'string' && itemName.toLowerCase().includes(searchTerm.toLowerCase());
        const filteredChildren = item.children ? filterChecklist(item.children) : [];
        if (itemMatches || filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      })
      .filter((item) => item !== null) as TagModel[];
  };

  // Flatten the tag hierarchy for easier lookup.
  const flatList = flattenChecklist(tags);

  // Callback to handle a tag item change (currently no-op; update as needed).
  const handleTagItemChange = useCallback((event: TagModel, isChecked: boolean) => {
    // Implement tag change handling logic here if needed.
  }, []);

  // Remove a tag from the selected tags.
  const handleRemoveTag = (id: number) => {
    setCheckedItems((prev) => ({ ...prev, [id]: false }));
    const tagItem = flatList.find((item) => item.id === id);
    if (tagItem) {
      handleTagItemChange(tagItem, false);
    }
  };

  // Handle checkbox change for tags.
  const handleCheckboxChange = (id: number, children?: TagModel[]) => {
    setCheckedItems((prev) => {
      const isChecked = !prev[id];
      const updates = { ...prev, [id]: isChecked };

      if (children) {
        children.forEach((child) => {
          updates[child.id] = isChecked;
        });
      }

      const tagItem = flatList.find((item) => item.id === id);
      if (tagItem) {
        handleTagItemChange(tagItem, isChecked);
      }

      return updates;
    });
  };

  // Fetch tags with hierarchy from the API.
  useEffect(() => {
    (async () => {
      if (apiClient.tagsClient?.getLevelTags) {
        const [newTags] = await Promise.all([apiClient.tagsClient.getLevelTags()]);
        setTags(newTags);
      }
    })();
  }, [apiClient.tagsClient]);
  // -----------------------------
  // End Tagging Feature
  // -----------------------------

  useEffect(() => {
    // Get required settings based on location
    if (locationId) {
      apiClient.settingsClient.getSettings(true, null, locationId, 'msal-optional').then((res) => {
        const isRequired = res?.find((item) => item.name === 'PatientIdRequired')?.value === 'true';
        setPatientIdRequired(isRequired);
      });
    }
  }, [apiClient.settingsClient, locationId]);

  const handleSubmit = async ({ values, isValid }: SubmitParams): Promise<void> => {
    if (!isValid) {
      NotificationsService.displayError('Please enter the required fields');
      return;
    }

    // Validate the patient before saving
    if (!currentPatient) {
      NotificationsService.displayError('Please select a patient for the exam');
      return;
    }

    // Check if patient is valid
    const isPatientValid = validatePatient(currentPatient, patientIdRequired);
    if (!isPatientValid) {
      NotificationsService.displayError('Please complete the required patient information');

      // Expand the patient accordion and set it to edit mode
      if (typeof setPatientFormState === 'function') {
        setPatientFormState(FormStates.EDIT);
      }

      // Force focus on patient section by expanding the sidebar if collapsed
      if (!sidebarExpanded) {
        setSidebarExpanded(true);
      }

      return;
    }

    // Validation check: Use pendingFiles for new exams, fetchedFiles for existing ones
    if (!isFormInEditMode && pendingFiles.length === 0 && !values.imageURL) {
      NotificationsService.displayError('Please upload a file to the exam');
      return;
    }
    setIsSubmitting(true);

    try {
      // Get location id from the values.
      const locationObj = values.location as { id: number } | undefined;

      const examData: ExamModel = {
        ...values,
        location_Id: locationObj?.id,
        patient_id: currentPatient?.id,
        physician_Id: (values.physician as { id: number })?.id,
        serviceId: (values.service as { id: number })?.id,
        services: [
          ...((values.services as ServiceModel[] | null | undefined) ?? []),
          ...((values.services_services as ServiceModel[] | null | undefined) ?? []),
        ],
        // Convert selected tags to examTagMapping format
        examTagMapping: flatList
          .filter((tag) => checkedItems[tag.id])
          .map(
            (tag) =>
              ({
                tagId: tag.id,
                examId: exam?.id || 0,
              }) as ExamTagMapping,
          ),
      } as unknown as ExamModel; // Use unknown to bypass type checking since we're adding a property not in the model

      // Build dynamic form fields.
      const formData = _map(values.dynamicForms || values.forms || {}, (value, key) => {
        return {
          entityType: 'Exam',
          entityId: exam?.id ?? 0,
          fields: mapDataToNameValuePair(value),
          form: {
            id: parseInt(key, 10) || 0,
            name: `Form ${key}`,
            description: 'Dynamic Form',
            formFields: [],
            ui: '',
            data: '',
            reportTemplatePath: '',
            generateReport: false,
            autoSendReport: false,
            active: false,
          },
        };
      }) as unknown as DynamicFormModel[];

      // Include service details if provided.
      const serviceDetails = getServiceDetailsFromForm(values);
      if (Object.keys(serviceDetails).length > 0) {
        formData.push({
          entityType: 'Exam',
          entityId: exam?.id ?? 0,
          fields: mapDataToNameValuePair(serviceDetails as unknown as DynamicFormFieldModel),
        });
      }

      examData.dynamicForms = formData;

      let examIdToInvalidate: number | undefined = examIdNumber;
      let shouldRefetchExam = false;

      // Save or update the exam.
      if (isFormInEditMode && exam) {
        await apiClient.exams.updateExam(examData);
        setStatus(ExamApiStatus.UPDATED);
        NotificationsService.displaySuccess('Exam updated successfully');
        if (window.opener) {
          (window.opener as Window).postMessage({ type: 'examUpdated' }, '*');
        }
        examIdToInvalidate = exam.id;
        shouldRefetchExam = true;
      } else {
        // For new exams, include the IDs of files uploaded before saving
        const saveData = {
          ...examData,
          files: pendingFiles,
        };

        const newExamId = await apiClient.exams.saveExam(saveData);
        setStatus(ExamApiStatus.ADDED);
        NotificationsService.displaySuccess('Exam created successfully');
        if (window.opener) {
          (window.opener as Window).postMessage({ type: 'examCreated' }, '*');
        }
        examIdToInvalidate = newExamId;
        shouldRefetchExam = true;
      }

      // Invalidate the files query to refetch the list
      if (examIdToInvalidate) {
        await queryClient.invalidateQueries({ queryKey: ['files', 'by-exam-id', examIdToInvalidate] });
      }

      // Refetch exam details if needed (after save or update)
      if (shouldRefetchExam && examIdToInvalidate) {
        try {
          // If a new exam was successfully added, clear pending files
          if (status === ExamApiStatus.ADDED) {
            setPendingFiles([]);
          }
          const examResponse = await apiClient.exams.getExamById(examIdToInvalidate);
          setExam(examResponse);

          // Always update patient data regardless of whether it's a new exam or update
          if (examResponse?.patient_Id) {
            const patientResponse = await apiClient.patientClient.getPatient(examResponse.patient_Id);
            setPatient(patientResponse);
            setCurrentPatient(patientResponse ?? null);
            // Reset patient form to view mode to show updated data
            if (typeof setPatientFormState === 'function') {
              setPatientFormState(FormStates.DISABLED);
            }
          } else if (currentPatient?.id) {
            // If exam doesn't have patient_Id but we have a currentPatient, refresh it
            const refreshedPatient = await apiClient.patientClient.getPatient(currentPatient.id);
            setPatient(refreshedPatient);
            setCurrentPatient(refreshedPatient ?? null);
            // Reset patient form to view mode to show updated data
            if (typeof setPatientFormState === 'function') {
              setPatientFormState(FormStates.DISABLED);
            }
          }
        } catch (fetchError) {
          console.error('Error refetching exam after save/update:', fetchError);
          NotificationsService.displayError('Failed to refresh exam data after save.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      NotificationsService.displayError('Failed to save exam');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- File addition functions ---
  const handleAddNewFile = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = '.pdf,.xml,.png,.jpg,.tiff,.szi,.svs,.jpeg,.mp4,.atc,.mov';
    fileInput.onchange = handleFileInputChange;
    fileInput.click();
  };

  const handlePatientSaveSuccess = (patient: PatientModel) => {
    setCurrentPatient(patient);
    setPatient(patient);
  };

  const handleFileInputChange = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    // Get examId if it exists, otherwise pass null for new exams
    const currentExamId = id ? parseInt(id, 10) : null;

    setFileIsUploading(true);
    try {
      const formData = new FormData();
      for (const file of Array.from(input.files)) {
        formData.append('files', file);
      }

      // Use the correct arguments for the add method
      const uploadedFiles = await apiClient.filesClient.add_Deprecated(
        formData,
        false, // Assuming shouldSplitFiles is false here, adjust if needed
        currentExamId,
        patient?.id ?? null, // Pass patient ID if available
        DocumentCategories.EXAM.value, // Default to Exam category
        'msal-required',
      );

      if (uploadedFiles && uploadedFiles.length > 0) {
        setLastUploadedFileId(uploadedFiles[uploadedFiles.length - 1].id);
        if (currentExamId === null) {
          // New exam: Add to pending state
          setPendingFiles((prev) => [...prev, ...uploadedFiles]);
        } else {
          // Existing exam: Invalidate query
          await queryClient.invalidateQueries({ queryKey: ['files', 'by-exam-id', currentExamId] });
        }
        NotificationsService.displaySuccess('Files uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      NotificationsService.displayError('Failed to upload files');
    } finally {
      setFileIsUploading(false);
    }
  };

  // Function signature updated to accept examId: string | number
  const handleAddFiles = async (formData: FormData, shouldSplitFiles: boolean, examId?: string | number) => {
    // The examId parameter is added to match the expected prop type,
    // but we still use the 'id' from the component's scope for the actual logic.
    // Use component's 'id' to determine the actual exam ID, pass null for new exams
    const currentExamId = id ? parseInt(id, 10) : null;

    setFileIsUploading(true);
    try {
      // Ensure correct arguments are passed to the add method
      const uploadedFiles = await apiClient.filesClient.add_Deprecated(
        formData,
        shouldSplitFiles,
        currentExamId,
        patient?.id ?? null, // Pass patient ID if available
        DocumentCategories.EXAM.value, // Default to Exam category
        'msal-required',
      );

      if (uploadedFiles && uploadedFiles.length > 0) {
        setLastUploadedFileId(uploadedFiles[uploadedFiles.length - 1].id);
        if (currentExamId === null) {
          // New exam: Add to pending state
          setPendingFiles((prev) => [...prev, ...uploadedFiles]);
        } else {
          // Existing exam: Invalidate query
          await queryClient.invalidateQueries({ queryKey: ['files', 'by-exam-id', currentExamId] });
        }
        NotificationsService.displaySuccess('Files uploaded successfully');
      }
      return { files: uploadedFiles }; // Return in the expected format if needed downstream
    } catch (error) {
      console.error('Error uploading files:', error);
      NotificationsService.displayError('Failed to upload files');
      return null;
    } finally {
      setFileIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileId: string | number) => {
    if (!id) {
      NotificationsService.displayError('Cannot delete file: Exam ID is missing.');
      return false;
    }
    const currentExamId = parseInt(id, 10);
    const fileIdStr = fileId.toString(); // API expects string

    // --- Optimistic Update ---
    // 1. Cancel ongoing refetches for the files query
    await queryClient.cancelQueries({ queryKey: ['files', 'by-exam-id', currentExamId] });

    // 2. Get the current state of the files query
    const previousFiles = queryClient.getQueryData<FileModel[]>(['files', 'by-exam-id', currentExamId]);

    // 3. Optimistically update the cache
    queryClient.setQueryData<FileModel[]>(['files', 'by-exam-id', currentExamId], (oldData) => (oldData ? oldData.filter((file) => file.id !== fileId) : []));

    // 4. Determine the next current file optimistically - REMOVED setCurrentFile call
    // --- End Optimistic Update ---

    try {
      await apiClient.filesClient.deleteFile(typeof fileId === 'string' ? parseInt(fileId, 10) : fileId, 'msal-required');
      NotificationsService.displaySuccess('File deleted successfully');
      // Invalidate *after* successful API call to ensure consistency,
      // although optimistic update handles the immediate UI change.
      await queryClient.invalidateQueries({ queryKey: ['files', 'by-exam-id', currentExamId] });
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      NotificationsService.displayError('Failed to delete file');
      // --- Rollback on error ---
      if (previousFiles) {
        queryClient.setQueryData(['files', 'by-exam-id', currentExamId], previousFiles);
        // Rollback currentFile selection - REMOVED setCurrentFile call
      }
      // --- End Rollback ---
      return false;
    }
  };

  // --- Location dropdown management ---
  useEffect(() => {
    if (exam?.location?.id) {
      setLocationId(exam.location.id);
    }
  }, [exam?.location?.id]);

  // Effect to synchronize patient and currentPatient
  useEffect(() => {
    if (patient !== undefined && patient !== null) {
      setCurrentPatient(patient);
    }
    // Only run when patient changes, not when currentPatient changes
  }, [patient, setCurrentPatient]);

  useEffect(() => {
    const fetchPatient = async () => {
      if (exam?.patient_Id) {
        try {
          const patientResponse = await apiClient.patientClient.getPatient(exam.patient_Id);
          setPatient(patientResponse);
        } catch (error) {
          console.error('Error fetching patient:', error);
          // Optional: Notify user about patient fetch error
        }
      } else {
        // Clear patient if exam has no patient_Id
        // setPatient(undefined);
      }
    };
    fetchPatient();
  }, [apiClient.patientClient, exam?.patient_Id]);

  // Effect to fetch exam details
  useEffect(() => {
    const fetchExam = async () => {
      if (isFormInEditMode && id) {
        try {
          const examResponse = await apiClient.exams.getExamById(parseInt(id, 10));
          setExam(examResponse);

          // Initialize selected tags from examTagMapping if available
          // Access examTagMapping safely using type assertion
          type ExamResponseWithTagMapping = ExamModel & { examTagMapping?: ExamTagMapping[] };
          const examWithTags = examResponse as ExamResponseWithTagMapping;

          if (examWithTags.examTagMapping && examWithTags.examTagMapping.length > 0) {
            const selectedTagIds = examWithTags.examTagMapping.map((mapping: ExamTagMapping) => mapping.tagId);
            // Create a new checked items object with the selected tag IDs
            const newCheckedItems: Record<number, boolean> = {};
            selectedTagIds.forEach((tagId: number) => {
              newCheckedItems[tagId] = true;
            });
            setCheckedItems(newCheckedItems);
          }

          // Reset patient state before fetching new one based on exam
        } catch (error) {
          console.error('Error fetching exam:', error);
          NotificationsService.displayError('Failed to fetch exam');
          // Consider clearing exam state on fetch failure
          setExam(undefined);
        }
      } else {
        // Clear exam and related states if not in edit mode or no ID
        setExam(undefined);
        setCurrentFile(null);
        // Potentially reset other states like locationId if needed
      }
    };
    fetchExam();
    // Remove currentPatient from dependency array to prevent rerunning when patient is selected
  }, [apiClient.exams, id, isFormInEditMode, setCurrentPatient]);

  // Effect to ensure non-internal users only see the files tab
  useEffect(() => {
    if (!isInternal && (activeTab === 'sendQueue' || activeTab === 'uploadTracker' || activeTab === 'hl7Send')) {
      setActiveTab('files');
    }
  }, [isInternal, activeTab]);

  // --- Handle window close notification ---
  useEffect(() => {
    const handleClose = () => {
      if (window.opener) {
        (window.opener as Window).postMessage('examClosed', '*');
      }
    };
    window.addEventListener('beforeunload', handleClose);
    return () => {
      window.removeEventListener('beforeunload', handleClose);
    };
  }, []);

  // Handle exam status changes.
  useEffect(() => {
    if (status === ExamApiStatus.ADDED) {
      window.close();
    }
    return () => {
      setStatus(null);
    };
  }, [status]);

  return (
    <CollapsibleSidebar
      header={<Breadcrumb data={breadcrumbs} />}
      body={
        <ExamProcessingSidebarBody
          currentPatient={currentPatient}
          exam={exam ?? null}
          handleSubmit={handleSubmit as unknown as () => void}
          isFormInEditMode={isFormInEditMode}
          patientFormState={patientFormState}
          patientSearchText={patientSearchText}
          setCurrentPatient={setCurrentPatient}
          setPatientFormState={setPatientFormState as unknown as (state: string) => void}
          setPatientSearchText={setPatientSearchText}
          isInternal={isInternal}
          isSubmitting={isSubmitting}
          onLocationIdChange={setLocationId}
          tags={tags}
          checkedItems={checkedItems}
          handleCheckboxChange={handleCheckboxChange}
          handleRemoveTag={handleRemoveTag}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterChecklist={filterChecklist}
          flatList={flatList}
          onPatientSaveSuccess={handlePatientSaveSuccess}
          initialActiveKeys={
            isFormInEditMode && Object.values(checkedItems).some((checked) => checked) ? [...EDIT_EXAM_ACTIVE_KEYS_INITIAL, TAGS_ACCORDION_KEY] : undefined
          }
        />
      }
      expanded={sidebarExpanded}
      onExpandChange={setSidebarExpanded}
    >
      {showPatientsDialog && (
        <SelectPatientModal
          dataState={patientSearchDataState}
          patients={searchPatients}
          onClose={() => setShowPatientsDialog(false)}
          onCreateNew={() => handleCreateNewPatientClick(null)}
          onDataStateChange={handlePatientsSearchDataStateChange}
          onPatientSelect={handlePatientSelect}
          total={totalSearchPatients}
        />
      )}
      <StyledDivContent>
        <ExamProcessingHeader
          integrations={exam?.location?.integrations as Integration[] | undefined}
          email={exam?.location?.email ?? ''}
          examId={examIdNumber}
          fax={exam?.location?.fax ?? ''}
          file={currentFile}
          setCurrentFile={setCurrentFile}
          hasReadHistory={!_isEmpty(exam?.reads)}
          locationHasIntegrations={!!exam?.location?.integrations?.length}
          locationHasDicomIntegration={!!exam?.location?.integrations?.some((s) => s.name === 'Dicom')}
          hasSuid={!!exam?.suid}
          physicianEmail={exam?.physician?.email ?? ''}
          sidebarExpanded={sidebarExpanded}
          isInternal={isInternal}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        {activeTab === 'files' && currentFile && <FileViewer key={currentFile.id} file={currentFile} />}
        {activeTab === 'activityLog' && isInternal && <ActivityLogTab entityType="Exam" id={examIdNumber} />}
        {activeTab === 'examStatusLog' && isInternal && <ExamStatusLogTab id={examIdNumber} />}
        {activeTab === 'sendQueue' && <SendQueue examId={examIdNumber} />}
        {activeTab === 'uploadTracker' && <UploadTrackerHome examId={examIdNumber} hideHeader={true} />}
        {activeTab === 'hl7Send' && <HL7SendHome examId={examIdNumber} />}
        {activeTab === 'hipaaLog' && isInternal && <HippaLogs examId={examIdNumber} />}
        {activeTab === 'files' && !currentFile && displayFiles && displayFiles.length > 0 && (
          <CenteredMessage>Please select a file from the right sidebar.</CenteredMessage>
        )}
        {activeTab === 'files' && !currentFile && (!displayFiles || displayFiles.length === 0) && (
          <CenteredMessage>No files associated with this exam.</CenteredMessage>
        )}
      </StyledDivContent>
      <FileSelectSidebar
        files={displayFiles ?? []}
        handleFileSelect={(file) => {
          setCurrentFile(file);
          // Optionally switch back to files tab when a file is selected
          // setActiveTab('files');
        }}
        handleAddNewFileClick={handleAddNewFile}
        selectedFile={currentFile}
        showDeleteBtn
        showEditBtn
        examType={exam?.service?.description ?? undefined}
        examId={id ?? undefined}
        isInternal={isInternal}
        onAddFiles={handleAddFiles}
        onDeleteFile={handleDeleteFile}
      />
    </CollapsibleSidebar>
  );
};

const StyledDivContent = styled.div`
  flex: 1 1 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content 1fr;
  overflow: hidden;
  contain: strict;
  background-color: ${({ theme }) => theme.colors.backgroundPrimary};
`;

const StyledIcon = styled(SvgIcon)`
  color: ${({ theme }) => theme.colors.primary};
`;

const CenteredMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;
